import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatChipsModule } from "@angular/material/chips";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";

import { IDamageAssessmentResponse } from "../../interfaces/i-damage-assessment-response";
import { IPropertyDamageAssessmentStructure } from "../../interfaces/i-property-damage-assessment-structure";
import { IPropertyStructure } from "../../interfaces/i-property-structure";
import { IFeatureChip } from "../../interfaces/i-feature-chip";
import { IFootprintVisibility } from "../../interfaces/i-footprint-visibility";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { DamageStructureCardComponent } from "./damage-structure-card/damage-structure-card.component";
import { DocumentService } from "../../services/internal-services/document.service";
import { buildDetectedChips } from "../../utils/feature-chips.util";
import { PROPERTY_FEATURE_CHIP_LABELS } from "../../config/property-feature-chip-labels.config";

@Component({
  selector: "lib-damage-overview",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./damage-overview.component.html",
  styleUrls: ["./damage-overview.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    MatExpansionModule,
    MatChipsModule,
    MatProgressBarModule,
    MatButton,
    MatIconButton,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    MatOptionModule,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    DamageStructureCardComponent,
  ],
})
export class DamageOverviewComponent {
  @Input() set damageData(value: IDamageAssessmentResponse) {
    this._damageData = value;
    this.buildStructures(value);
  }

  get damageData(): IDamageAssessmentResponse {
    return this._damageData;
  }

  /** Currently selected measurement system ID. */
  @Input() measurementSystem: number;

  /** Current footprint and label visibility state. */
  @Input() visibilityValues: IFootprintVisibility = { propertyFootprint: true, labelsFootprint: true };

  /** Whether map discrete mode is active. */
  @Input() discreteEnabled = false;

  /** Emits the new measurement system ID selected by the user. */
  @Output() measurementSystemChange = new EventEmitter<number>();

  /** Emits updated visibility state when the user toggles a checkbox. */
  @Output() visibilityChange = new EventEmitter<IFootprintVisibility>();

  /** Emits when the user clicks a zoom button, carrying the WKT geometry and padding. */
  @Output() zoomRequested = new EventEmitter<{ wkt: string; paddingPx: number }>();

  readonly measurementSystems = ViewerConstants.MEASUREMENT_SYSTEMS.IDS;

  structures: IPropertyDamageAssessmentStructure[] = [];

  preStructures: IPropertyStructure[] = [];

  expandedPropertyFeatures = false;

  showAllPropertyFeatures = false;

  private _damageData: IDamageAssessmentResponse;

  get parcelWkt(): string | undefined {
    return this.damageData?.pre_catastrophe?.property_features?.parcel_wkt;
  }

  get damageEventDate(): string | undefined {
    const primary = this.structures.find((s) => s.primaryStructure) ?? this.structures[0];
    return primary?.source_image_metadata?.capture_date;
  }

  get preDamageDate(): string | undefined {
    const primary = this.preStructures.find((s) => s.primaryStructure) ?? this.preStructures[0];
    return primary?.source_image_metadata?.capture_date;
  }

  /** Sorts post-catastrophe structures: primary first, then others. */
  private buildStructures(data: IDamageAssessmentResponse): void {
    if (!data?.post_catastrophe?.structure) return;
    const raw = data.post_catastrophe.structure;
    const primary = raw.filter((s) => s.primaryStructure);
    const others = raw.filter((s) => !s.primaryStructure);
    this.structures = [...primary, ...others];
    this.preStructures = data.pre_catastrophe?.structures ?? data.pre_catastrophe?.structure ?? [];
  }

  /** Formats an area from square metres to the user's preferred unit string. */
  formatArea(m2: number): string {
    if (m2 == null) return "-";
    return this.measurementSystem === this.measurementSystems.IMPERIAL
      ? `${(m2 * 10.764).toFixed(1)} ft²`
      : `${(+m2).toFixed(1)} m²`;
  }

  calculateTotalArea(): number {
    return this.preStructures.reduce((acc, s) => acc + (s.footPrint?.area ?? 0), 0);
  }

  /**
   * Builds IFeatureChip[] from pre_catastrophe property_features.
   * Uses the same logic as PropertyOverviewComponent.
   */
  getPropertyFeatures(): IFeatureChip[] {
    const features = this.damageData?.pre_catastrophe?.property_features;
    if (!features) return [];
    return buildDetectedChips(features, "", PROPERTY_FEATURE_CHIP_LABELS, this.formatArea.bind(this))
      .filter((c) => this.showAllPropertyFeatures || c.detected)
      .sort((a, b) => Number(b.detected) - Number(a.detected));
  }

  onVisibilityChange(key: keyof IFootprintVisibility, checked: boolean): void {
    this.visibilityChange.emit({ ...this.visibilityValues, [key]: checked });
  }

  onMeasurementSystemChange(value: number): void {
    this.measurementSystemChange.emit(value);
  }

  /** Copies the given value to the clipboard. */
  copyToClipboard(value: string): void {
    DocumentService.copyTextToClipboard(value);
  }
}
